<template>
    <div
        v-dark
        class="mode"
        :class="{
            'mode--qotd-answered': isQotdAnswered,
            'mode--assignments': name === 'Assignments'
        }"
        tabindex="0"
        role="button"
        :aria-label="`Start ${name} ${ modeNumber !== '5' ? 'quiz' : ''}${ isBeta ? ', Beta' : '' }`"
        :aria-disabled="name === 'Question of the Day' && isQotdAnswered ? 'true' : 'false'"
        @click="(name !== 'Question of the Day' || !isQotdAnswered) && clickMode()"
        @keydown.enter="(name !== 'Question of the Day' || !isQotdAnswered) && clickMode()"
        @mousedown.prevent
    >
        <Tag
            v-if="isBeta"
            class="mode__beta"
            :class="{
                'mode__beta--premium': isPremium && !hasActiveSubscription,
            }"
            :size="'small'"
            :is-beta-tag="true"
            :is-dark-mode="isDarkMode"
        >
            <template #tagMessage>
                Beta
            </template>
        </Tag>
        <Tag
            v-if="!isLoading && isPremium && !hasActiveSubscription"
            class="mode__premium"
            :size="'small'"
            :is-dark-mode="isDarkMode"
        >
            <template #tagMessage>
                Premium
            </template>
        </Tag>
        <Tag
            v-if="!isLoading && showLevelUpFreeTrialTag"
            class="mode__free-trial"
            :class="{
                'mode__free-trial--premium': isPremium && !hasActiveSubscription,
            }"
            :size="'small'"
            :is-beta-tag="true"
            :is-dark-mode="isDarkMode"
        >
            <template #tagMessage>
                Free Trial
            </template>
        </Tag>
        <div
            class="mode__icon"
            :class="{
                [`mode__icon--${modeIcon}`]: true,
                [`mode__icon--${modeIcon}--dark`]: isDarkMode,
                'mode__icon--qotd-answered': isQotdAnswered,
                'mode__icon--qotd-answered--dark': isQotdAnswered && isDarkMode,
                'mode__icon--assignments': name === 'Assignments'
            }"
            :style="{ color: modeColor }"
        >
            <Icon :type="modeIcon" />
        </div>
        <div
            v-dark
            class="mode__name"
            :class="{ 'mode__name--qotd-answered': isQotdAnswered }"
        >
            {{ name }}
        </div>
        <div
            v-if="name === 'Question of the Day'"
            v-dark
            :class="{ 'mode__qotd--qotd-answered': isQotdAnswered }"
            class="mode__qotd"
        >
            {{ todaysDate }}
        </div>
        <div
            v-if="name === 'Assignments' && numberOfAssignments"
            v-dark
            class="mode__assignments"
            :class="{ 'mode__assignments--double-digits': numberOfAssignments > 9 }"
        >
            {{ numberOfAssignments }}
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { subscriptionModule } from '@/store/subscription/module'
import UIKit, { utils } from '@pocketprep/ui-kit'
import { userModule } from '@/store/user/module'
import { qotdModule } from '@/store/qotd/module'
import { months } from '@/utils'

const {
    Icon,
    Tag,
} = UIKit

const props = withDefaults(
    defineProps<{
        isPremium?: boolean
        isBeta?: boolean
        name: string
        numberOfAssignments?: number
        showLevelUpFreeTrialTag?: boolean
    }>(),
    {
        isPremium: false,
        isBeta: false,
        numberOfAssignments: undefined,
        showLevelUpFreeTrialTag: false,
    }
)

const emit = defineEmits<{
    click: [modeName: string]
}>()

const isLoading = ref(true)

const todaysDate = computed(() => {
    const month = new Date().getMonth()

    return `${months[month]?.slice(0, 3)} ${new Date().getDate()}`
})

const hasActiveSubscription = computed(() => {
    return subscriptionModule.getters.getSubscriptionForExamId()
})

const isQotdAnswered = computed(() => {
    return (!!qotdModule.getters.getQotDQuiz() || isLoading.value) && props.name === 'Question of the Day'
})

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const modeNumber = computed(() => {
    let modeNumberVal: keyof typeof utils.studyModes = '10'
    if (props.name === 'Quick 10') {
        modeNumberVal = '0'
    } else if (props.name === 'Timed Quiz') {
        modeNumberVal = '4'
    } else if (props.name === 'Question of the Day') {
        modeNumberVal = '-1'
    } else if (props.name === 'Weakest Subject') {
        modeNumberVal = '3'
    } else if (props.name === 'Missed Questions') {
        modeNumberVal = '2'
    } else if (props.name === 'Mock Exam') {
        modeNumberVal = '5'
    } else if (props.name === 'Level Up') {
        modeNumberVal = '6'
    } else if (props.name === 'Assignments') {
        modeNumberVal = '11'
    }

    return modeNumberVal
})

const modeColor = computed(() => {
    return isDarkMode.value
        ? utils.studyModes[modeNumber.value].iconColorDM
        : utils.studyModes[modeNumber.value].iconColor
})

const modeIcon = computed(() => {
    return utils.studyModes[modeNumber.value].icon
})

onMounted(async () => {
    await Promise.all([
        userModule.actions.fetchUserData(),
        qotdModule.actions.fetchCurrentQotDQuestion(),
    ])
    isLoading.value = false
})

const clickMode = () => {
    emitClick(props.name)
}

const emitClick = (modeName: string) => {
    emit('click', modeName)
}
</script>
<style lang="scss" scoped>
.mode {
    width: 158px;
    height: 106px;
    border-radius: 6px;
    border: 1px solid rgba($pewter, 0.85);
    box-shadow: 0 1px 4px 0 rgba($ash, 0.3);
    box-sizing: border-box;
    align-items: center;
    justify-content: center;
    display: flex;
    user-select: none;
    position: relative;
    flex-direction: column;
    outline: none;
    background: $white;

    @include breakpoint(brown-bear) {
        width: 222px;
        height: 114px;
    }

    @include breakpoint(black-bear) {
        width: 100%;
        height: 50px;
        flex-direction: row;
        justify-content: flex-start;
    }

    &--assignments {
        &:hover {
            outline: 2px solid $slate-01;
            cursor: pointer;
        }

        @include breakpoint(brown-bear) {
            width: 698px;
            height: 50px;
            display: flex;
            flex-direction: row;
            justify-content: flex-start;
            align-items: center;

        }

        @include breakpoint(black-bear) {
            width: 100%;
            height: 50px;
            flex-direction: row;
            justify-content: flex-start;
        }
    }

    &:not(&--qotd-answered):focus {
        border: 1px solid $brand-blue;
    }

    &:not(&--qotd-answered, &--assignments):hover {
        border: 2px solid $slate-01;
        cursor: pointer;

        .mode__premium {
            top: 2px;
            left: 2px;

            @include breakpoint(black-bear) {
                top: 50%;
                right: 11px;
                left: auto;
            }
        }

        .mode__beta,
        .mode__free-trial {
            top: 2px;
            right: 2px;

            @include breakpoint(black-bear) {
                top: 50%;
                right: 11px;
                left: auto;
            }

            &--premium {
                @include breakpoint(black-bear) {
                    top: 50%;
                    right: 74px;
                    left: auto;
                }
            }
        }

        .mode__qotd {
            top: 17px;
            right: 13px;
        }
    }

    &--dark {
        background: $brand-black;
        border-color: $slate;
        box-shadow: 0 1px 4px 0 rgba($jet, 0.3);
        color: $white;

        &:not(&.mode--qotd-answered):hover {
            border-color: $brand-black;

            &::before {
                content: '';
                left: -2px;
                top: -2px;
                position: absolute;
                width: 100%;
                height: 100%;
                border: 2px solid $white;
                opacity: 0.6;
                border-radius: 6px;
            }
        }

        &:not(&.mode--qotd-answered):focus {
            border-color: $brand-black;

            &::before {
                content: '';
                left: -2px;
                top: -2px;
                position: absolute;
                width: calc(100% + 2px);
                height: calc(100% + 2px);
                border: 1px solid $banana-bread;
                border-radius: 6px;
            }
        }
    }

    &__icon--subject {
        svg {
            height: 26px;
            width: 20px;
        }
    }

    &__premium,
    &__beta,
    &__free-trial {
        position: absolute;
        top: 3px;
        left: 3px;

        @include breakpoint(black-bear) {
            top: 50%;
            margin-top: -9px;
            right: 12px;
            left: auto;
        }
    }

    &__beta,
    &__free-trial {
        left: auto;
        right: 3px;

        @include breakpoint(black-bear) {
            right: 12px;
        }
        &--premium {
            @include breakpoint(black-bear) {
                right: 75px;
            }
        }
    }

    &__icon {
        margin-bottom: 8px;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;

        @include breakpoint(brown-bear) {
            margin-bottom: 18px;
        }

        @include breakpoint(black-bear) {
            margin: 0 12px;
        }

        &--qotd-answered {
            color: $steel !important;

            &--dark {
                color: $slate !important;
            }
        }

        &--assignments {
            @include breakpoint(brown-bear) {
                margin: 0 12px;
            }
        }
    }

    &__name {
        font-size: 14px;
        font-weight: 600;
        line-height: 17px;

        &--qotd-answered {
            color: $slate;

            &--dark {
                color: $steel;
            }
        }
    }

    &__qotd {
        color: $brand-blue;
        position: absolute;
        right: 14px;
        top: 18px;
        display: none;
        font-size: 14px;

        @include breakpoint(black-bear) {
            display: block;
        }

        &::after {
            content: '';
            width: 7px;
            height: 7px;
            background: $brand-blue;
            border-radius: 7px;
            margin-left: 5px;
            display: inline-block;
            margin-bottom: 1px;
        }

        &--dark {
            color: $banana-bread;

            &::after {
                background: $banana-bread;
            }
        }

        &--qotd-answered {
            color: $slate;

            &::after {
                display: none;
            }

            &--dark {
                color: $steel;
            }
        }
    }

    &__assignments {
        display: none;
        position: absolute;
        right: 14px;
        width: 22px;
        height: 22px;
        flex-shrink: 0;
        border-radius: 11px;
        border: 1px solid $butterscotch;
        background: $buttermilk;
        color: $brand-black;
        font-size: 14px;
        font-weight: 600;
        line-height: 19px;


        @include breakpoint(brown-bear) {
            display: flex;
            justify-content: center;
            align-items: center;
        }

        &--dark {
            color: $banana-bread;
            border-radius: 11px;
            border: 1px solid $banana-bread;
            background: rgba($buttermilk, 0.20);
        }

        &--double-digits {
            width: 31px;
        }
    }
}
</style>
